import React, { useEffect, useRef, useState } from 'react';

import '../ui/css/SidebarNavigation.css';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Sidebar } from 'primereact/sidebar';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { version } from '../constants';
import profilePic from '../profilePic.svg';
import { RootState } from '../state/store';
import { SidebarNavigationProps } from '../types/props';
import { useVisibilityClasses } from '../utils';

import CustomMenuItem from './CustomMenuItem';
import FeedbackCard from './FeedbackCard';
import GlobalSettingsDialog from './GlobalSettingsDialog';
import LogoutCard from './LogoutCard';
import { withDialogFooterSpacing } from './shared/dialogFooterSpacing';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  sidebarVisible,
  setSidebarVisible,
  passedClassName
}) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const licenseInfo = useSelector((state: RootState) => state.license.info);
  const navigate = useNavigate();
  const visibilityClasses = useVisibilityClasses();
  const [logoutDialogVisible, setLogoutDialogVisible] = useState(false);
  const [feedbackDialogVisible, setFeedbackDialogVisible] = useState(false);
  const [globalSettingsDialogVisible, setGlobalSettingsDialogVisible] =
    useState(false);
  const feedbackClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (feedbackClickTimerRef.current) {
        clearTimeout(feedbackClickTimerRef.current);
        feedbackClickTimerRef.current = null;
      }
    };
  }, []);

  const licenseUntilText = React.useMemo(() => {
    const dt = licenseInfo?.lockDateTime;
    if (!dt) return '—';
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return '—';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }, [licenseInfo?.lockDateTime]);

  const isGlobalSettingsButtonVisible =
    visibilityClasses['wholeui__roles'] ||
    visibilityClasses['globalSetting-nav-btn__roles'] ||
    localStorage.getItem('isTestMode') === 'true';

  return (
    <div className="sidebar">
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        style={{ width: '22rem' }}
      >
        <div
          className={`sidebar-inner-wrapper flex h-full flex-column justify-content-between ${passedClassName}`}
        >
          <div className="sidebar-main">
            <div className="text-400 text-sm" style={{position: 'absolute', top: 25}}>v. {version} Лицензия до: {licenseUntilText}</div>
            
            <div className="isUser flex flex-row gap-3 align-items-center">
              <div className="isUser-icon flex-shrink-0" style={{height: 55, width: 55, borderRadius: '50%', position: 'relative'}}>
                <img style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} src={profilePic} alt="any-image" />
              </div>
              <div className="userData flex flex-column w-full">
                <div className="userRole">{userInfo?.displayName}</div>
                <div className="text-sm text-500">{userInfo?.login}</div>
              </div>
            </div>
            
            <div className="mt-3">
              <Divider className="mt-2 mb-2" />
              <div className="mb-1">Участки</div>
              <ul style={{ maxHeight: '100px', overflowY: 'auto'}} className="pl-3 space-y-0.5 m-0 text-sm departments-list">
                {userInfo?.departments?.map((department, index) => (
                  <li key={index}>{department?.name}</li>
                ))}
              </ul>
            </div>

            <CustomMenuItem />
          </div>

          <div className="sidebar-footer">
            <div className="flex align-items-center justify-content-between w-full gap-2">
              <Button
                severity="secondary"
                text
                tooltip="Просмотр обратной связи пользователей мобильного приложения"
                className="show-feedback-btn__roles"
                onClick={() => {
                  if (feedbackClickTimerRef.current) {
                    clearTimeout(feedbackClickTimerRef.current);
                  }
                  feedbackClickTimerRef.current = setTimeout(() => {
                    setFeedbackDialogVisible(true);
                  }, 250);
                }}
                onDoubleClick={() => {
                  if (feedbackClickTimerRef.current) {
                    clearTimeout(feedbackClickTimerRef.current);
                    feedbackClickTimerRef.current = null;
                  }
                  setFeedbackDialogVisible(false);
                  setSidebarVisible(false);
                  navigate('/feedback');
                }}
              >
                Обратная связь
                <i className="ml-2 pi pi-comment"></i>
              </Button>

              <div>
                {isGlobalSettingsButtonVisible && (
                  <Button
                    icon="pi pi-cog"
                    className="p-button-text p-button-rounded globalSetting-nav-btn__roles"
                    tooltip="Общесистемные настройки"
                    onClick={() => {
                      setGlobalSettingsDialogVisible(true);
                    }}
                  />
                )}
                              
                <Button
                  tooltip="Выход из системы"
                  style={{ marginTop: '4px' }}
                  className="p-button-danger p-button-text"
                  icon={'pi pi-sign-out'}
                  onClick={() => {
                    setLogoutDialogVisible(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <Dialog
          header="Обратная связь"
          visible={feedbackDialogVisible}
          style={{
            minWidth: '75%',
            width: '75%',
            minHeight: '780px',
            height: '780px',
            maxHeight: '780px'
          }}
          modal
          contentStyle={{ minHeight: '100px' }}
          pt={withDialogFooterSpacing()}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button
                severity="secondary"
                type="button"
                label="Открыть на странице"
                icon="pi pi-external-link pi-right"
                onClick={() => {
                  setFeedbackDialogVisible(false);
                  setSidebarVisible(false);
                  navigate('/feedback');
                }}
              />
            </div>
          }
          onHide={() => setFeedbackDialogVisible(false)}
        >
          <FeedbackCard variant="dialog" />
        </Dialog>

        <Dialog
          header="Вы уверены, что хотите выйти из системы?"
          visible={logoutDialogVisible}
          style={{ minWidth: '543px', width: '543px', minHeight: '150px' }}
          modal
          contentStyle={{ minHeight: '100px' }}
          onHide={() => setLogoutDialogVisible(false)}
        >
          <LogoutCard
            onReturn={() => {
              setLogoutDialogVisible(false);
            }}
            onLogout={() => {
              setLogoutDialogVisible(false);
              setSidebarVisible(false);
            }}
          />
        </Dialog>

        <GlobalSettingsDialog
          visible={globalSettingsDialogVisible}
          onHide={() => setGlobalSettingsDialogVisible(false)}
        />
      </Sidebar>
    </div>
  );
};

export default SidebarNavigation;